import { formatDate } from "@yatayat/core/date";
import { toNepaliDigits, type Lang } from "@yatayat/core/money";
import { StaffRole } from "@yatayat/gen/yatayat/auth/v1/auth_pb";
import { Channel, PaymentMethod, TicketStatus } from "@yatayat/gen/yatayat/booking/v1/booking_pb";
import type { LocalizedName } from "@yatayat/gen/yatayat/types/v1/types_pb";
import { VehicleClass } from "@yatayat/gen/yatayat/types/v1/types_pb";

import type { MessageKey } from "~/lib/i18n";

// Other services' enums are labelled in staff-labels and complaint-labels, so a page that
// needs only a helper from here doesn't download those services' descriptors.

export const vehicleClassLabels: Record<VehicleClass, MessageKey> = {
  [VehicleClass.UNSPECIFIED]: "classOrdinary",
  [VehicleClass.ORDINARY]: "classOrdinary",
  [VehicleClass.DELUXE]: "classDeluxe",
  [VehicleClass.SUPER_DELUXE]: "classSuperDeluxe",
};

export const paymentMethodLabels: Partial<Record<PaymentMethod, MessageKey>> = {
  [PaymentMethod.WALLET]: "payWallet",
  [PaymentMethod.QR]: "payQr",
  [PaymentMethod.BANK]: "payBank",
  [PaymentMethod.CASH]: "payCash",
};

export const ticketStatusLabels: Partial<Record<TicketStatus, MessageKey>> = {
  [TicketStatus.HELD]: "statusHeld",
  [TicketStatus.CONFIRMED]: "statusConfirmed",
  [TicketStatus.CANCELLED]: "statusCancelled",
  [TicketStatus.EXPIRED]: "statusExpired",
  [TicketStatus.RELEASED]: "statusReleased",
};

export const channelLabels: Partial<Record<Channel, MessageKey>> = {
  [Channel.WEB]: "channelWeb",
  [Channel.COUNTER]: "channelCounter",
  [Channel.PARTNER]: "channelPartner",
};

export const staffRoleLabels: Record<StaffRole, MessageKey> = {
  [StaffRole.UNSPECIFIED]: "roleStaff",
  [StaffRole.OPERATOR_ADMIN]: "roleOperatorAdmin",
  [StaffRole.COUNTER_CLERK]: "roleCounterClerk",
  [StaffRole.CONDUCTOR]: "roleConductor",
  [StaffRole.REGULATOR_ADMIN]: "roleRegulatorAdmin",
  [StaffRole.PLANNER]: "rolePlanner",
  [StaffRole.INSPECTOR]: "roleInspector",
};

// Regulator staff act for one of these; the session carries only the id.
const authorityLabels: Partial<Record<string, MessageKey>> = {
  dotm: "consoleAuthorityDotm",
};

/** The name of the authority a regulator acts for, or its id when it has no label. */
export function authorityName(authorityId: string, t: (key: MessageKey) => string): string {
  const key = authorityLabels[authorityId];
  return key ? t(key) : authorityId.toUpperCase();
}

/** Short weekday names, Sunday first, as the server numbers days 0 to 6. */
export const weekdayLabels: MessageKey[] = ["daySun", "dayMon", "dayTue", "dayWed", "dayThu", "dayFri", "daySat"];

/** Formats a Nepal calendar date, "YYYY-MM-DD", for the reader: "2 July 2026", or BS "१७ असार २०८३" in Nepali. */
export function formatIsoDate(date: string, lang: Lang): string {
  // Noon in Nepal, so the date reads the same in any time zone.
  return formatDate(Date.parse(`${date}T12:00:00+05:45`), lang);
}

/** Writes the digits of codes such as seat numbers and masked phones in the reader's script. */
export function localDigits(s: string, lang: Lang): string {
  return lang === "ne" ? toNepaliDigits(s) : s;
}

/** A place's name in the current language, falling back to its code. */
export function placeName(place: { code: string; name?: LocalizedName } | undefined, lang: Lang): string {
  return place?.name?.[lang] || place?.code || "";
}

/** Minutes and seconds left, "09:05". */
export function formatCountdown(ms: number, lang: Lang): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const s = `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;

  return localDigits(s, lang);
}
